import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Video, FileText, Paperclip, HelpCircle, Upload, Layers, ChevronUp, ChevronDown, Pencil, Sparkles, FileUp } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";
const TYPE_META = {
  video: { icon: Video, label: "Video" },
  text: { icon: FileText, label: "Reading" },
  resource: { icon: Paperclip, label: "Resource" },
  quiz: { icon: HelpCircle, label: "Quiz" },
};

const blankQuestion = () => ({ question: "", qtype: "single", options: ["", ""], correct_index: 0, accepted_answers: [""], explanation: "" });

const blankLesson = (type) => ({
  title: "", type,
  video_source: "youtube", video_url: "", file_path: "", file_name: "", duration: "",
  body: "", resource_name: "",
  questions: [blankQuestion()], pass_mark: 70, is_diagnostic: false,
  hint: "", faqs: [],
});

const QTYPES = [
  { v: "single", label: "Multiple choice" },
  { v: "truefalse", label: "True / False" },
  { v: "fill_blank", label: "Fill in the blank" },
];

const noNulls = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === null ? "" : v]));

function LessonForm({ moduleId, initial, lessonId, onSaved, onCancel }) {
  const [type, setType] = useState(initial?.type || "video");
  const normQ = (q) => ({ ...blankQuestion(), ...q, options: q.options?.length ? q.options : ["", ""], accepted_answers: q.accepted_answers?.length ? q.accepted_answers : [""] });
  const initF = () => {
    if (!initial) return blankLesson("video");
    const base = { ...blankLesson(initial.type), ...noNulls(initial) };
    if (!Array.isArray(base.faqs)) base.faqs = [];
    if (base.questions?.length) base.questions = base.questions.map(normQ);
    else base.questions = [blankQuestion()];
    return base;
  };
  const [f, setF] = useState(initF);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const changeType = (t) => { setType(t); setF({ ...blankLesson(t), title: f.title }); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/lms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("file_path", data.path);
      set("file_name", data.name);
      if (type === "resource" && !f.resource_name) set("resource_name", data.name);
      if (type === "video") set("video_source", "upload");
      toast.success("File uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const setQ = (qi, patch) => setF((p) => ({ ...p, questions: p.questions.map((q, i) => i === qi ? { ...q, ...patch } : q) }));
  const setOpt = (qi, oi, v) => setF((p) => ({ ...p, questions: p.questions.map((q, i) => i === qi ? { ...q, options: q.options.map((o, j) => j === oi ? v : o) } : q) }));
  const changeQType = (qi, t) => {
    if (t === "truefalse") setQ(qi, { qtype: t, options: ["True", "False"], correct_index: 0 });
    else if (t === "single") setQ(qi, { qtype: t, options: ["", ""], correct_index: 0 });
    else setQ(qi, { qtype: t });
  };
  const setAcc = (qi, ai, v) => setF((p) => ({ ...p, questions: p.questions.map((q, i) => i === qi ? { ...q, accepted_answers: q.accepted_answers.map((a, j) => j === ai ? v : a) } : q) }));
  const setFaq = (i, patch) => setF((p) => ({ ...p, faqs: (p.faqs || []).map((x, j) => j === i ? { ...x, ...patch } : x) }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Add a lesson title");
    try {
      const body = { ...f, type };
      body.faqs = (f.faqs || []).filter((x) => (x.q || "").trim() && (x.a || "").trim());
      if (type !== "quiz") delete body.questions;
      if (lessonId) await api.patch(`/lms/lessons/${lessonId}`, body);
      else await api.post(`/lms/modules/${moduleId}/lessons`, body);
      toast.success(lessonId ? "Lesson updated" : "Lesson added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save lesson"); }
  };

  return (
    <div className="border border-[var(--line)] p-3 bg-[var(--alt)] space-y-3" data-testid="lesson-form">
      <input data-testid="lesson-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Lesson title" className={inputCls} />
      <div className="flex flex-wrap gap-2">
        {Object.entries(TYPE_META).map(([t, m]) => (
          <button key={t} data-testid={`lesson-type-${t}`} onClick={() => changeType(t)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border ${type === t ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}>
            <m.icon size={13} /> {m.label}
          </button>
        ))}
      </div>

      {type === "video" && (
        <div className="space-y-2">
          <div className="flex gap-2 text-xs">
            {["youtube", "vimeo", "upload"].map((s) => (
              <label key={s} className="inline-flex items-center gap-1">
                <input type="radio" name={`vsrc-${moduleId}`} checked={f.video_source === s} onChange={() => set("video_source", s)} /> {s}
              </label>
            ))}
          </div>
          {f.video_source !== "upload" ? (
            <input data-testid="lesson-video-url" value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="Paste YouTube/Vimeo embed or share URL" className={inputCls} />
          ) : (
            <label className="inline-flex items-center gap-2 text-sm border border-dashed border-[var(--line)] px-3 py-2 cursor-pointer hover:border-[var(--ink)]">
              <Upload size={14} /> {uploading ? "Uploading…" : f.file_name || "Upload video file"}
              <input type="file" accept="video/*" className="hidden" data-testid="lesson-video-file" onChange={(e) => upload(e.target.files[0])} />
            </label>
          )}
          <input value={f.duration} onChange={(e) => set("duration", e.target.value)} placeholder="Duration e.g. 8 min (optional)" className={inputCls} />
        </div>
      )}

      {type === "text" && (
        <textarea data-testid="lesson-body" value={f.body} onChange={(e) => set("body", e.target.value)} rows={5} placeholder="Lesson reading content…" className={inputCls} />
      )}

      {type === "resource" && (
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm border border-dashed border-[var(--line)] px-3 py-2 cursor-pointer hover:border-[var(--ink)]">
            <Upload size={14} /> {uploading ? "Uploading…" : f.file_name || "Upload PDF / document"}
            <input type="file" className="hidden" data-testid="lesson-resource-file" onChange={(e) => upload(e.target.files[0])} />
          </label>
          <input value={f.resource_name} onChange={(e) => set("resource_name", e.target.value)} placeholder="Display name (optional)" className={inputCls} />
          <p className="text-xs text-[var(--ink-2)]">PDF slides and images display inline as the main lesson; other files show as a download.</p>
        </div>
      )}

      {type === "quiz" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <label className="flex items-center gap-2">Pass mark %
              <input type="number" value={f.pass_mark} onChange={(e) => set("pass_mark", parseInt(e.target.value || 0, 10))} className="w-20 px-2 py-1 border border-[var(--line)]" data-testid="quiz-pass-mark" />
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer" title="Pre-test: records a score but never blocks completion or the certificate">
              <input type="checkbox" checked={!!f.is_diagnostic} onChange={(e) => set("is_diagnostic", e.target.checked)} data-testid="quiz-diagnostic-toggle" />
              <span className="font-semibold">Diagnostic / pre-test</span>
            </label>
          </div>
          {f.is_diagnostic && <p className="text-xs text-[var(--ink-2)]">A diagnostic quiz checks starting knowledge. Learners see their score and explanations, but it never affects completion or the certificate.</p>}
          {f.questions.map((q, qi) => (
            <div key={qi} className="border border-[var(--line)] bg-white p-2 space-y-2" data-testid={`quiz-q-${qi}`}>
              <div className="flex gap-2 items-center">
                <input value={q.question} onChange={(e) => setQ(qi, { question: e.target.value })} placeholder={`Question ${qi + 1}`} className={inputCls} />
                <select value={q.qtype} onChange={(e) => changeQType(qi, e.target.value)} data-testid={`quiz-q-${qi}-type`} className="px-2 py-2 border border-[var(--line)] text-xs shrink-0">
                  {QTYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
                {f.questions.length > 1 && <button onClick={() => setF((p) => ({ ...p, questions: p.questions.filter((_, i) => i !== qi) }))} className="text-[var(--ink-2)] hover:text-[#B71C1C]" data-testid={`quiz-q-${qi}-del`}><Trash2 size={14} /></button>}
              </div>

              {q.qtype === "fill_blank" ? (
                <div className="space-y-1.5">
                  <p className="text-xs text-[var(--ink-2)]">Accepted answers (any one counts; case-insensitive)</p>
                  {q.accepted_answers.map((a, ai) => (
                    <div key={ai} className="flex items-center gap-2">
                      <input value={a} onChange={(e) => setAcc(qi, ai, e.target.value)} placeholder={`Accepted answer ${ai + 1}`} className={inputCls} data-testid={`quiz-q-${qi}-acc-${ai}`} />
                      {q.accepted_answers.length > 1 && <button onClick={() => setQ(qi, { accepted_answers: q.accepted_answers.filter((_, j) => j !== ai) })} className="text-[var(--ink-2)] hover:text-[#B71C1C]"><Trash2 size={13} /></button>}
                    </div>
                  ))}
                  <button onClick={() => setQ(qi, { accepted_answers: [...q.accepted_answers, ""] })} className="text-xs text-[var(--accent)] font-semibold">+ accepted answer</button>
                </div>
              ) : (
                <>
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input type="radio" name={`correct-${moduleId}-${qi}`} checked={q.correct_index === oi} onChange={() => setQ(qi, { correct_index: oi })} title="Correct answer" data-testid={`quiz-q-${qi}-correct-${oi}`} />
                      <input value={o} onChange={(e) => setOpt(qi, oi, e.target.value)} placeholder={`Option ${oi + 1}`} className={inputCls} readOnly={q.qtype === "truefalse"} />
                    </div>
                  ))}
                  {q.qtype === "single" && <button onClick={() => setQ(qi, { options: [...q.options, ""] })} className="text-xs text-[var(--accent)] font-semibold">+ option</button>}
                </>
              )}

              <textarea value={q.explanation} onChange={(e) => setQ(qi, { explanation: e.target.value })} rows={2} placeholder="Explanation shown after answering (optional)" className={inputCls} data-testid={`quiz-q-${qi}-explanation`} />
            </div>
          ))}
          <button onClick={() => setF((p) => ({ ...p, questions: [...p.questions, blankQuestion()] }))} className="text-xs font-semibold border border-[var(--line)] px-3 py-1.5 hover:border-[var(--ink)]">+ Add question</button>
        </div>
      )}

      <div className="border-t border-[var(--line)] pt-3 space-y-2">
        <p className="text-xs font-semibold inline-flex items-center gap-1.5"><HelpCircle size={13} className="text-[var(--accent)]" /> Learner help (free — shown if a learner gets stuck)</p>
        <textarea value={f.hint || ""} onChange={(e) => set("hint", e.target.value)} rows={2} placeholder="Optional hint, revealed when a learner clicks 'Need a hint?'" className={inputCls} data-testid="lesson-hint" />
        {(f.faqs || []).map((fq, i) => (
          <div key={i} className="border border-[var(--line)] bg-white p-2 space-y-1.5" data-testid={`lesson-faq-${i}`}>
            <div className="flex items-center gap-2">
              <input value={fq.q || ""} onChange={(e) => setFaq(i, { q: e.target.value })} placeholder={`FAQ question ${i + 1}`} className={inputCls} />
              <button onClick={() => setF((p) => ({ ...p, faqs: p.faqs.filter((_, j) => j !== i) }))} className="text-[var(--ink-2)] hover:text-[#B71C1C]"><Trash2 size={14} /></button>
            </div>
            <textarea value={fq.a || ""} onChange={(e) => setFaq(i, { a: e.target.value })} rows={2} placeholder="Answer" className={inputCls} />
          </div>
        ))}
        <button onClick={() => setF((p) => ({ ...p, faqs: [...(p.faqs || []), { q: "", a: "" }] }))} className="text-xs font-semibold text-[var(--accent)]" data-testid="add-faq">+ Add FAQ</button>
      </div>

      <div className="flex gap-2">
        <button onClick={save} data-testid="lesson-save" className="bg-[var(--accent)] text-white text-sm font-semibold px-4 py-2 hover:bg-[var(--accent-hover)]">{lessonId ? "Save changes" : "Add lesson"}</button>
        <button onClick={onCancel} className="text-sm px-4 py-2 border border-[var(--line)] hover:border-[var(--ink)]">Cancel</button>
      </div>
    </div>
  );
}

function ImportPanel({ courseId, onApplied }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [draft, setDraft] = useState(null);

  const analyse = async () => {
    if (!file) return toast.error("Choose a document first");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/lms/courses/${courseId}/import-draft`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const modules = (data.modules || []).map((m) => ({ include: true, title: m.title, lessons: (m.lessons || []).map((l) => ({ include: true, ...l })) }));
      setDraft({ source_name: data.source_name, modules });
      toast.success("Draft ready — review, then add to your curriculum");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not analyse that file"); }
    setLoading(false);
  };

  const setM = (mi, patch) => setDraft((d) => ({ ...d, modules: d.modules.map((m, i) => i === mi ? { ...m, ...patch } : m) }));
  const setL = (mi, li, patch) => setDraft((d) => ({ ...d, modules: d.modules.map((m, i) => i === mi ? { ...m, lessons: m.lessons.map((l, j) => j === li ? { ...l, ...patch } : l) } : m) }));

  const apply = async () => {
    const modules = draft.modules.filter((m) => m.include)
      .map((m) => ({ title: m.title, lessons: m.lessons.filter((l) => l.include).map(({ include, ...l }) => l) }))
      .filter((m) => m.lessons.length);
    if (!modules.length) return toast.error("Select at least one lesson to add");
    setApplying(true);
    try {
      const { data } = await api.post(`/lms/courses/${courseId}/import-apply`, { modules });
      toast.success(`Added ${data.modules_created} module(s) · ${data.lessons_created} lesson(s)`);
      onApplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add to curriculum"); }
    setApplying(false);
  };

  return (
    <div className="border border-[var(--accent)] bg-[var(--alt)] p-3 mb-4 space-y-3" data-testid="import-panel">
      <p className="text-sm font-display font-bold inline-flex items-center gap-1.5"><Sparkles size={15} className="text-[var(--accent)]" /> AI import from a document</p>
      <p className="text-xs text-[var(--ink-2)]">Upload course material (.docx, .pptx, .pdf, .txt). AI drafts modules, reading lessons and quizzes for you to review before adding.</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 text-sm border border-dashed border-[var(--line)] bg-white px-3 py-2 cursor-pointer hover:border-[var(--ink)]">
          <FileUp size={14} /> {file ? file.name : "Choose document"}
          <input type="file" accept=".docx,.pptx,.pdf,.txt" className="hidden" data-testid="import-file" onChange={(e) => { setFile(e.target.files[0]); setDraft(null); }} />
        </label>
        <button onClick={analyse} disabled={loading} data-testid="import-analyse" className="bg-[var(--accent)] text-white text-sm font-semibold px-4 py-2 hover:bg-[var(--accent-hover)] disabled:opacity-60 inline-flex items-center gap-1.5">
          <Sparkles size={14} /> {loading ? "Analysing…" : "Analyse with AI"}
        </button>
      </div>

      {draft && (
        <div className="space-y-3 pt-1" data-testid="import-draft">
          <p className="text-xs text-[var(--ink-2)]">Drafted from <span className="font-semibold">{draft.source_name}</span>. Untick anything you don't want.</p>
          {draft.modules.map((m, mi) => (
            <div key={mi} className="border border-[var(--line)] bg-white p-2 space-y-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={m.include} onChange={(e) => setM(mi, { include: e.target.checked })} data-testid={`import-mod-${mi}-include`} />
                <input value={m.title} onChange={(e) => setM(mi, { title: e.target.value })} data-testid={`import-mod-${mi}-title`} className={`${inputCls} font-semibold`} />
              </div>
              <div className="pl-6 space-y-1.5">
                {m.lessons.map((l, li) => (
                  <div key={li} className="flex items-start gap-2 text-sm border-b border-[var(--line)] pb-1.5 last:border-0">
                    <input type="checkbox" checked={l.include} onChange={(e) => setL(mi, li, { include: e.target.checked })} data-testid={`import-les-${mi}-${li}-include`} className="mt-1.5" />
                    <span className="mt-1.5">{l.type === "quiz" ? <HelpCircle size={13} className="text-[var(--accent)]" /> : <FileText size={13} className="text-[var(--accent)]" />}</span>
                    <div className="flex-1">
                      <input value={l.title} onChange={(e) => setL(mi, li, { title: e.target.value })} data-testid={`import-les-${mi}-${li}-title`} className={inputCls} />
                      <p className="text-xs text-[var(--ink-2)] mt-0.5">
                        {l.type === "quiz"
                          ? <>{(l.questions || []).length} question(s){l.is_diagnostic ? " · diagnostic pre-test" : ""}</>
                          : <>{(l.body || "").slice(0, 90)}{(l.body || "").length > 90 ? "…" : ""}</>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button onClick={apply} disabled={applying} data-testid="import-apply" className="bg-[var(--ink)] text-white text-sm font-semibold px-4 py-2 hover:bg-black disabled:opacity-60 inline-flex items-center gap-1.5">
            <Plus size={14} /> {applying ? "Adding…" : "Add to curriculum"}
          </button>
        </div>
      )}
    </div>
  );
}

export function CurriculumManager({ course }) {
  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [newModule, setNewModule] = useState("");
  const [addingTo, setAddingTo] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/lms/courses/${course.id}/curriculum`);
    setModules(data.modules || []);
  }, [course.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addModule = async () => {
    if (!newModule.trim()) return;
    await api.post(`/lms/courses/${course.id}/modules`, { title: newModule.trim() });
    setNewModule(""); load();
  };
  const delModule = async (id) => { await api.delete(`/lms/modules/${id}`); load(); };
  const delLesson = async (id) => { await api.delete(`/lms/lessons/${id}`); load(); };
  const moveModule = async (id, direction) => { await api.post(`/lms/modules/${id}/move`, { direction }); load(); };
  const moveLesson = async (id, direction) => { await api.post(`/lms/lessons/${id}/move`, { direction }); load(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button onClick={() => setOpen(true)} data-testid={`curriculum-btn-${course.id}`} className="text-xs font-semibold border border-[var(--ink)] bg-[var(--ink)] text-white px-3 py-1.5 hover:bg-black w-full inline-flex items-center justify-center gap-1.5 mt-2">
        <Layers size={13} /> Manage curriculum
      </button>
      <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="curriculum-dialog">
        <DialogHeader><DialogTitle className="font-display">Curriculum: {course.title}</DialogTitle></DialogHeader>

        <button onClick={() => setShowImport((v) => !v)} data-testid="toggle-import" className="text-xs font-semibold border border-[var(--accent)] text-[var(--accent)] px-3 py-1.5 hover:bg-[var(--accent)] hover:text-white inline-flex items-center gap-1.5 mb-3 w-full justify-center">
          <Sparkles size={13} /> {showImport ? "Hide AI import" : "AI import from a document"}
        </button>
        {showImport && <ImportPanel courseId={course.id} onApplied={() => { setShowImport(false); load(); }} />}

        <div className="flex gap-2 mb-3">
          <input data-testid="new-module-input" value={newModule} onChange={(e) => setNewModule(e.target.value)} placeholder="New module title (e.g. Module 1: Foundations)" className={inputCls} />
          <button onClick={addModule} data-testid="add-module-btn" className="bg-[var(--accent)] text-white text-sm font-semibold px-4 hover:bg-[var(--accent-hover)] inline-flex items-center gap-1"><Plus size={14} /> Module</button>
        </div>

        {modules.length === 0 && <p className="text-sm text-[var(--ink-2)] py-6 text-center">No modules yet. Add your first module to start building lessons.</p>}

        <div className="space-y-4">
          {modules.map((m, mi) => (
            <div key={m.id} className="border border-[var(--line)]" data-testid={`module-${m.id}`}>
              <div className="flex items-center justify-between bg-[var(--alt)] px-3 py-2">
                <span className="font-display font-bold text-sm">{m.title}</span>
                <div className="flex items-center gap-1.5 text-[var(--ink-2)]">
                  <button onClick={() => moveModule(m.id, "up")} disabled={mi === 0} data-testid={`module-up-${m.id}`} className="hover:text-[var(--ink)] disabled:opacity-30"><ChevronUp size={15} /></button>
                  <button onClick={() => moveModule(m.id, "down")} disabled={mi === modules.length - 1} data-testid={`module-down-${m.id}`} className="hover:text-[var(--ink)] disabled:opacity-30"><ChevronDown size={15} /></button>
                  <button onClick={() => delModule(m.id)} data-testid={`del-module-${m.id}`} className="hover:text-[#B71C1C]"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="p-3 space-y-2">
                {(m.lessons || []).map((ls, li) => {
                  const Icon = (TYPE_META[ls.type] || TYPE_META.text).icon;
                  if (editingLesson === ls.id) {
                    return <LessonForm key={ls.id} moduleId={m.id} initial={ls} lessonId={ls.id} onSaved={() => { setEditingLesson(null); load(); }} onCancel={() => setEditingLesson(null)} />;
                  }
                  return (
                    <div key={ls.id} className="flex items-center justify-between text-sm border-b border-[var(--line)] pb-2" data-testid={`lesson-row-${ls.id}`}>
                      <span className="inline-flex items-center gap-2"><Icon size={14} className="text-[var(--accent)]" /> {ls.title}</span>
                      <div className="flex items-center gap-1.5 text-[var(--ink-2)]">
                        <button onClick={() => moveLesson(ls.id, "up")} disabled={li === 0} data-testid={`lesson-up-${ls.id}`} className="hover:text-[var(--ink)] disabled:opacity-30"><ChevronUp size={14} /></button>
                        <button onClick={() => moveLesson(ls.id, "down")} disabled={li === m.lessons.length - 1} data-testid={`lesson-down-${ls.id}`} className="hover:text-[var(--ink)] disabled:opacity-30"><ChevronDown size={14} /></button>
                        <button onClick={() => { setAddingTo(null); setEditingLesson(ls.id); }} data-testid={`edit-lesson-${ls.id}`} className="hover:text-[var(--ink)]"><Pencil size={13} /></button>
                        <button onClick={() => delLesson(ls.id)} className="hover:text-[#B71C1C]"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  );
                })}
                {addingTo === m.id ? (
                  <LessonForm moduleId={m.id} onSaved={() => { setAddingTo(null); load(); }} onCancel={() => setAddingTo(null)} />
                ) : (
                  <button onClick={() => { setEditingLesson(null); setAddingTo(m.id); }} data-testid={`add-lesson-${m.id}`} className="text-xs font-semibold text-[var(--accent)] inline-flex items-center gap-1"><Plus size={13} /> Add lesson</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
